import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import { useSeo } from '@/hooks/useSeo';
import { useJsonLd } from '@/hooks/useJsonLd';
import { works } from '@/mocks/works';
import { characters } from '@/mocks/characters';

export default function WorksPage() {
  useSeo({
    title: '作品专题 - 妖精动漫收录作品一览 | 妖精动漫网',
    description: '妖精动漫网作品专题，收录风之精灵使、花冠物语、星野之森、月光回廊等妖精、精灵类动漫作品的简介、评分、年份与收录角色，方便按作品查找角色百科。',
  });

  useJsonLd({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '妖精动漫作品专题',
    inLanguage: 'zh-CN',
    about: '妖精与精灵类动漫作品',
  });

  const countFor = (workId: string) => characters.filter((c) => c.workId === workId).length;
  const [featured, ...rest] = works;

  return (
    <PageLayout>
      <section className="relative overflow-hidden pt-16 md:pt-18">
        <img
          src="https://readdy.ai/api/search-image?query=stylized%20artistic%20banner%20of%20fantasy%20anime%20film%20posters%20with%20emerald%20and%20gold%20glow%2C%20film%20reel%20and%20sparkles%2C%20soft%20painterly%20magical%20atmosphere%2C%20clean%20minimal%20composition&width=1600&height=400&seq=works-banner&orientation=landscape"
          alt="翠绿与金色调的电影胶卷与海报奇幻背景"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/85 to-primary-800/75" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <nav aria-label="面包屑" className="mb-3 flex items-center gap-1.5 text-xs text-background-100/80">
            <Link to="/" className="hover:text-accent-300">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">作品专题</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50">作品专题</h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base leading-6 text-background-100/90">
            每部作品都对应着一群生动的妖精角色。从作品出发，快速定位其中的角色百科与配音阵容。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-12">
        {/* 主打作品大卡 */}
        <article className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-6 overflow-hidden rounded-2xl border border-background-200/70 bg-background-50">
          <div className="relative aspect-video lg:aspect-auto lg:h-full min-h-56 overflow-hidden">
            <img src={featured.image} alt={`${featured.name} - ${featured.subtitle}`} className="h-full w-full object-cover" />
            <span className="absolute left-4 top-4 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-primary-900">主编推荐</span>
          </div>
          <div className="p-6 md:p-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">{featured.year}</span>
              <span className="text-sm text-foreground-500">·</span>
              <span className="text-sm text-foreground-500">{featured.genre}</span>
              <span className="ml-auto flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-sm font-bold text-accent-900">
                <i className="ri-star-fill text-xs" />{featured.rating}
              </span>
            </div>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-black text-foreground-950">{featured.name}</h2>
            <p className="text-sm text-foreground-500">{featured.subtitle} · {featured.episodes} 集 · 收录 {featured.characterCount} 位角色</p>
            <p className="mt-4 text-sm md:text-base leading-7 text-foreground-700">{featured.description}</p>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {featured.tags.map((t) => (
                <span key={t} className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800">{t}</span>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/characters" className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap hover:bg-primary-600 transition-colors">
                查看该作角色
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </article>

        {/* 其余作品 */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((work) => (
            <article key={work.id} className="group flex flex-col overflow-hidden rounded-xl border border-background-200/70 bg-background-50 transition-transform hover:-translate-y-1">
              <div className="relative aspect-video overflow-hidden">
                <img src={work.image} alt={`${work.name} - ${work.subtitle}`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-accent-500 px-2.5 py-1 text-xs font-bold text-primary-900">
                  <i className="ri-star-fill text-[11px]" />{work.rating}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-5">
                <h3 className="font-heading text-lg font-bold text-foreground-950">{work.name}</h3>
                <p className="mt-0.5 text-xs text-foreground-500">{work.year} · {work.genre} · {work.episodes} 集</p>
                <p className="mt-3 flex-1 text-sm leading-6 text-foreground-600">{work.description}</p>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex flex-wrap gap-1.5">
                    {work.tags.slice(0, 2).map((t) => (
                      <span key={t} className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800">{t}</span>
                    ))}
                  </div>
                  <Link to="/characters" className="inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-600 whitespace-nowrap">
                    查角色
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </PageLayout>
  );
}